import {observer} from 'mobx-react-lite'
import React, {useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Time} from 'yougile-util'
import {PermissionsChecker} from '@client/permissions-checker'
import {Backend} from '@common/backend'
import {ButtonNew} from '@components/common/base/buttons'
import {
  rowHeight,
  useTimelineConfig,
  useTimelineNewStore
} from '@components/timeline-new/timeline-new-store'
import {LinkData, TimelineScale} from '@components/timeline-new/types'
import {noPropagate} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {DeadlineCtl} from '@extensions/sticker/controllers/deadline-ctl'
import {Svg} from '../svg'
import {AutoPlanningMenu} from './auto-planning-menu'
import {
  hoverFilter,
  hoverFilterId,
  selectedFilter,
  selectedFilterId
} from './filters'
import {ArrowType} from './types'
import {
  arrowEnd,
  centerToBottom,
  centerToTop,
  prepareArrow,
  prepareButtonPlacement
} from './utils'

const TwRemoveButton = tw(ButtonNew)`
  shadow-md
  !py-2
  !pr-8
  !pl-4
  !h-[20px]
  !rounded-4
  !text-12
  !font-normal
`

const TwSvg = tw(Svg)`
  absolute
  pointer-events-none
  top-0
  left-0
`

export interface TaskBoxLinkProps {
  data: LinkData
}

// eslint-disable-next-line max-lines-per-function
export const TaskBoxLink = observer(({data}: TaskBoxLinkProps) => {
  const [state, store] = useTimelineNewStore()
  const config = useTimelineConfig()
  const [hover, setHover] = useState(false)

  const fromSwimlane = data.fromSwimlane
  const toSwimlane = data.toSwimlane
  const fromRow = state.tableRows[fromSwimlane]
  const toRow = state.tableRows[toSwimlane]
  if (!fromRow || !toRow) {
    return null
  }

  const fromData = state.itemDataRegistry[fromRow.data.hubId]
  const toData = state.itemDataRegistry[toRow.data.hubId]
  if (
    !fromData ||
    !toData ||
    !fromData.deadline.end ||
    !toData.deadline.start
  ) {
    return null
  }

  let gap = 0
  let gapEditable = false
  if (state.autoPlanning && fromData.row.hub && toData.row.hub) {
    const dependencies = toData.row.hub.data.dependencies ?? {}
    const dependency = dependencies[fromData.row.hub.id]
    if (typeof dependency === 'object') {
      gap = dependency.gap ?? 0
    }

    gapEditable = PermissionsChecker.checkEditTaskStickers(
      toData.row.hub,
      'not-user'
    )
  }
  const isGapLink = gap !== 0

  const draggingFrom = state.draggingPath === fromRow.data.path
  const draggingTo = state.draggingPath === toRow.data.path
  let fromTimestamp = draggingFrom ? state.draggingEnd : fromData.deadline.end
  let toTimestamp = draggingTo
    ? state.draggingStart
    : toData.deadline.start ?? toData.deadline.end
  // Add 1ms so that rounding works properly on 23:59:59.999 time
  fromTimestamp = store.endTimeToSnappedTime(fromTimestamp) + 1
  toTimestamp = store.startTimeToSnappedTime(toTimestamp)

  // Calculate if a link is invalid before adjusting timestamps
  const isInvalid = !state.autoPlanning && fromTimestamp > toTimestamp

  let curveDt = Time.day
  let arrowOffset = -2.5
  let timePadding = 2 * Time.day
  let expandableTsAdjustment = Time.day
  let expandableDxAdjustment = 4
  if (state.scale === TimelineScale.Days) {
    expandableDxAdjustment = 20
  } else if (state.scale === TimelineScale.Months) {
    curveDt = 2 * Time.day
    arrowOffset = -1.5
    timePadding = 4 * Time.day
    expandableTsAdjustment = 3 * Time.day
    expandableDxAdjustment = 5
  } else if (state.scale === TimelineScale.Years) {
    curveDt = 7 * Time.day
    arrowOffset = -1.5
    timePadding = 10 * Time.day
    expandableTsAdjustment = 9 * Time.day
    expandableDxAdjustment = 0
  }

  if (toData.row.expandable) {
    toTimestamp -= expandableTsAdjustment
  }

  const leftTimestamp = Math.min(fromTimestamp, toTimestamp) - timePadding
  const rightTimestamp = Math.max(fromTimestamp, toTimestamp) + timePadding

  const dSwimlane = toSwimlane - fromSwimlane
  const dt = toTimestamp - fromTimestamp

  const height = (Math.abs(dSwimlane) + 1) * rowHeight
  const width = store.intervalToDistance(rightTimestamp - leftTimestamp)
  const top = Math.min(fromSwimlane, toSwimlane) * rowHeight
  const left = store.timeToPosition(leftTimestamp)
  const transform = `translate(${left}px,${top}px)`

  const isCurved = toTimestamp - fromTimestamp < curveDt
  let arrowType: ArrowType = isCurved ? 'curved-bottom' : 'bottom'
  let y0 = centerToTop
  let y1 = height - centerToBottom
  if (fromSwimlane > toSwimlane) {
    arrowType = isCurved ? 'curved-top' : 'top'
    y0 = height - centerToBottom
    y1 = centerToTop
  }
  const dy = rowHeight * (Math.abs(dSwimlane) - 1) * Math.sign(dSwimlane)
  const x0 = store.intervalToDistance(fromTimestamp - leftTimestamp)
  const dx = store.intervalToDistance(dt)
  let x1 = store.intervalToDistance(toTimestamp - leftTimestamp)
  let ddx = 0
  if (toData.row.expandable) {
    ddx += expandableDxAdjustment
    x1 += expandableDxAdjustment
  }
  const [arrow, arrowIntervals] = prepareArrow(state.scale, arrowType)

  const path = arrow(x0, y0, dx, dy, ddx)

  const isAnySelected = !!state.editingLinkFromPath && !!state.editingLinkToPath
  const isThisSelected =
    isAnySelected &&
    state.editingLinkFromPath === data.fromPath &&
    state.editingLinkToPath === data.toPath
  const isOtherSelected = isAnySelected && !isThisSelected
  const canLink = !!fromData.row.linkable && !!toData.row.linkable

  let menuTransform: string | undefined
  if (isThisSelected) {
    const intervals = arrowIntervals(x0, y0, dx, dy, ddx)
    const [p] = prepareButtonPlacement(intervals, state.editingClickPoint)
    menuTransform = `translate(${p.x}px,${p.y}px) translateY(-50%) translateX(-50%)`
  }

  const isDragging = !!state.draggingPath
  const isLinking = !!state.linkingPath
  const isHoverable = !isDragging && !isLinking && !config.static

  let isBlockedDrag = false
  if (state.draggingPath === data.toPath && toData.row.hub) {
    const blockedPoints = DeadlineCtl.getBlockedPoints(toData.row.hub)
    isBlockedDrag = blockedPoints.includes('start')
  }

  return (
    <div
      style={{transform}}
      className={cn(
        'absolute',
        hover && 'z-1000',
        isThisSelected && 'z-[2000]'
      )}
    >
      <TwSvg width={width} height={height}>
        {/*The displayed arrow*/}
        <g
          className={cn(
            isBlockedDrag &&
              '!fill-notification-primary !stroke-notification-primary',
            isGapLink &&
              !isThisSelected &&
              !hover &&
              'fill-acid-violet stroke-acid-violet',
            isInvalid &&
              !isThisSelected &&
              !hover &&
              'fill-status-error stroke-status-error',
            !isInvalid &&
              !isGapLink &&
              !isThisSelected &&
              !hover &&
              'fill-acid-gray stroke-acid-gray',
            (isThisSelected || hover) &&
              'fill-notification-primary stroke-notification-primary',
            isOtherSelected && !hover && 'opacity-20'
          )}
          filter={cn(
            isThisSelected && `url(#${selectedFilterId})`,
            !isThisSelected && hover && `url(#${hoverFilterId})`
          )}
        >
          <circle cx={x0 + 3} cy={y0} r={1.5} stroke="none" />
          <path
            strokeWidth={1}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray={isBlockedDrag || isInvalid ? '2,2' : undefined}
            d={path}
            fill="none"
          />
          <path
            strokeWidth={1}
            strokeLinecap="round"
            strokeLinejoin="round"
            d={arrowEnd(x1, y1, arrowOffset)}
            fill="none"
          />
        </g>
        {/*The click zone for the arrow*/}
        <path
          onMouseOver={() => isHoverable && setHover(true)}
          onMouseOut={() => setHover(false)}
          strokeWidth={17}
          d={path}
          fill="none"
          stroke="none"
          className={cn(
            isHoverable && 'cursor-pointer [pointer-events:stroke]'
          )}
          onMouseDown={noPropagate}
          onClick={e => store.startLinkEdit(data.fromPath, data.toPath, e)}
        />
        {isThisSelected && selectedFilter}
        {!isThisSelected && hover && hoverFilter}
      </TwSvg>
      {isThisSelected && canLink && (
        <div className="absolute" style={{transform: menuTransform}}>
          <TwRemoveButton
            icon="IconTrash"
            title={Lang.msg.timeline.removeLink}
            color="notification-primary"
            onMouseDown={e => {
              e.stopPropagation()
              store.removeLink(fromData.row.hubId, toData.row.hubId)
            }}
          />
          {Backend.features.ganttLag &&
            state.autoPlanning &&
            toData.row.hub &&
            gapEditable && (
              <AutoPlanningMenu
                gap={gap}
                className="absolute left-0 top-[22px]"
              />
            )}
        </div>
      )}
    </div>
  )
})
